import ctaMan from "assets/images/psp/inlineCTA.jpg";
import illustrationSearch from "assets/images/psp/illustration-search.svg";
import GeorgeRansom from "assets/images/psp/review-george.png";
import SMurphy from "assets/images/psp/review-murphy.png";
import FemaleAvatar from "assets/images/psp/femaleavatar.jpg";
import StoreWineImage from "assets/images/psp/store-wine.webp";
import StoreWhiskeyImage from "assets/images/psp/store-whiskey.webp";
import SevenWaysCCImage from "assets/images/psp/7-ways-cc.webp";
import HeaderBackgroundDesktop from "assets/images/psp/hero/wine-hero.webp";
import HeaderBackgroundMobile from "assets/images/psp/hero/wine-header-mobile.webp";
import HeaderBackgroundTablet from "assets/images/psp/hero/wine-header-tablet.webp";
import { storageCategories } from "pages/PSPPage/utils/getPSPLinks";

const faqs = [
	{
		question: "What's the ideal temperature and humidity for wine storage?",
		answer: "The optimal temperature for wine storage is between 55-58°F, with minimal fluctuation (no more than 5-degree swings). This temperature allows wine to age gracefully without accelerating the process. Humidity should be maintained between 60-70% to keep corks moist and prevent air infiltration."
	},
	{
		question:
			"How much does professional wine storage cost compared to building a home cellar?",
		answer: "The cost of wine storage varies significantly by location and facility type, but typically ranges from $25-$300 monthly depending on capacity and amenities. A basic wine locker for 50 bottles might cost $40-60 monthly, while larger units accommodating 200+ bottles range $150-250."
	},
	{
		question: "Can I access my wine collection anytime I want?",
		answer: "Most wine storage facilities offer convenient access hours, typically 7am-7pm daily, with some providing 24/7 access for premium customers. Unlike restaurant wine programs, you maintain complete control over your collection and can retrieve bottles whenever needed."
	},
	{
		question:
			"What's the difference between wine storage and regular climate-controlled storage?",
		answer: "Wine storage facilities maintain much more precise environmental conditions than standard climate-controlled storage. While regular climate storage might fluctuate between 55-85°F, wine storage maintains strict 55-58°F temperatures with minimal variation."
	},
	{
		question:
			"How do I know if my wine collection needs professional storage?",
		answer: "Consider professional wine storage if you're storing wine for more than 6 months, have bottles worth over $50 each, live in a climate with significant temperature variations, or lack consistent cool storage space in your home."
	}
];

const relatedContentCards = [
	{
		category: "Self Storage",
		url: "https://www.storage.com/blog/how-to-store-wine/",
		date: "October 31, 2025",
		image: StoreWineImage,
		content: "Everything You Need to Know About How to Store Wine"
	},
	{
		category: "Self Storage",
		url: "https://www.storage.com/blog/how-to-store-whiskey/",
		date: "September 10, 2025",
		image: StoreWhiskeyImage,
		content:
			"How to Store Whiskey: Storage Ideas for Short-Term and Long-Term Whiskey Storage"
	},
	{
		category: "Self Storage",
		url: "https://www.storage.com/blog/climate-controlled-storage-units/",
		date: "December 18, 2025",
		image: SevenWaysCCImage,
		content: "Seven Ways to Use Climate Controlled Storage"
	}
];

const pspLinksContent = {
	title: "More Ways to Store",
	image: illustrationSearch,
	storageTypes: storageCategories.wineStorage
};

const reviewsContent = {
	title: "Storage.com has a 4.4 out of 5 star rating on",
	testimonials: [
		{
			body: 'Great experience with my storage! I love the "drive right in" climate-controlled storage area. Staff was super helpful with telling me about the system they use and setting me up.',
			author: "Jachooz",
			title: "Great experience with my storage",
			imageSrc: GeorgeRansom
		},
		{
			body: "Very knowledgeable and detail-oriented. The storage facility the Storage.com rep recommended is very close to where I live and at a reasonable rate. Climate-controlled and state-of-the-art security, which I can appreciate. Thanks again for all of your help and information.",
			author: "C. Meredith",
			title: "Excellent Customer Service",
			imageSrc: FemaleAvatar
		},
		{
			body: "I had never used a storage unit before and had many questions regarding the necessary size, locations, climate-controlled, etc. Storage.com's rep made sure all of my questions were answered and the reservation was made.",
			author: "Glen",
			title: "Excellent help when I needed it!",
			imageSrc: SMurphy
		}
	]
};

export const PSPWineContent = {
	type: "Wine",
	sections: [
		{
			title: "Hero Section",
			noPadding: true,
			modules: [
				{
					type: "hero-section",
					props: {
						title: "Find the Top Wine Storage Units",
						description:
							"Professional Wine Storage Solutions That Preserve Value and Flavor\n\nYour wine collection deserves better than closet storage or garage racks. [Climate-controlled](https://www.storage.com/climate-controlled/) wine storage facilities provide optimal temperature, humidity, and security conditions that home storage simply can't match.\n\nWhether you're a serious collector, investor, or enthusiast building your first cellar, professional wine storage solutions protect your investment.\n\nStorage.com connects you with verified wine storage facilities nationwide, so your collection ages gracefully while you focus on discovering your next favorite bottle.",
						searchPlaceholder:
							"Search by city, state, or zip...",
						buttonText: "Find Wine Storage",
						heroImages: {
							desktop: HeaderBackgroundDesktop,
							tablet: HeaderBackgroundTablet,
							mobile: HeaderBackgroundMobile
						},
						heroAltText: "Wine Storage Hero Image"
					}
				}
			]
		},
		{
			title: "Wine Storage Content",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Understanding Wine Storage Costs and Solutions",
						background: "light-blue",
						content: [
							{
								id: "1",
								value: "Every wine lover knows this frustration: You've invested in bottles you're excited to age, but your home lacks proper storage conditions. Temperature fluctuations ruin vintages, humidity issues dry out corks, and limited space forces you to pass up hot deals. Meanwhile, improperly stored wine loses value and flavor – costing you time, money, and excitement."
							},
							{
								id: "2",
								value: "The cost of wine storage through storage facilities is surprisingly affordable compared to building your own cellar or opting for other specialty wine cellars. Wine storage solutions start as low as $25 monthly for small collections, while a home wine cellar can cost $15,000-$50,000 to construct properly."
							},
							{
								id: "3",
								value: "Wine storage facilities solve storage problems with tailored climate control and secure environments designed specifically for wine preservation. Instead of risking your collection in basement corners or kitchen cabinets, you get professional-grade storage that maintains consistent red wine temperature storage conditions year-round."
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Wine Storage Unit Sizes",
			noPadding: true,
			modules: [
				{
					type: "size-chart",
					props: {
						background: "white",
						title: "Wine Storage Solutions by Collection Size",
						description:
							"Whether you're a new collector or managing a commercial wine inventory, there's a wine storage solution that fits your needs. Here's how different storage options accommodate various collection sizes.",
						storages: [
							{
								title: "Wine Lockers",
								bestFor: [
									"New collectors",
									"5x5 to 5x10, 25-50 bottles",
									"Personal favorites"
								]
							},
							{
								title: "Large Wine Units",
								bestFor: [
									"Collectors",
									"Wine businesses",
									"10x10 to 10x15, 100+ bottles"
								]
							},
							{
								title: "Custom Wine Rooms",
								bestFor: [
									"High-end collectors",
									"Commercial use",
									"10x20 and up, 500+ bottles"
								]
							}
						]
					}
				}
			]
		},
		{
			title: "How to Store Wine",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "How to Store Wine Without a Wine Cellar",
						background: "light-blue",
						content: [
							{
								id: "1",
								value: "Professional wine storage facilities replicate ideal cellar conditions without the construction costs or space requirements."
							},
							{
								id: "2",
								value: "These specialized facilities maintain consistent temperatures between 55-58°F, control humidity for wine storage at optimal 60-70% levels, and eliminate vibration that can disturb sediment in aging wines."
							},
							{
								id: "3",
								value: "Key advantages over home wine storage:\n* Precise climate control vs. seasonal temperature swings\n* Professional security systems and insurance options\n* Vibration-free environment for proper aging\n* Expandable space as your collection grows"
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Wine Storage Solutions Video",
			noPadding: true,
			modules: [
				{
					type: "video-content",
					props: {
						background: "blue",
						title: "Wine Collections That Thrive in Professional Storage",
						description:
							"Wine storage isn't just for serious collectors. Here's how different wine enthusiasts benefit from professional storage solutions.",
						mediaType: "video",
						mediaUrl: "enyJ_5ttrTc",
						videoTitle:
							"Wine Collections That Thrive in Professional Storage"
					}
				}
			]
		},
		{
			title: "Wine Storage Differences",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "What Makes Wine Storage Facilities Different",
						background: "blue",
						listType: "single",
						content: [
							{
								id: "1",
								value: "Unlike general storage, wine storage facilities and climate-controlled units cater specifically to wine preservation needs:"
							},
							{
								id: "2",
								value: '**Precise Temperature Control:** Consistent 55-58°F for optimal aging, not just "cool" temperatures'
							},
							{
								id: "3",
								value: "**Humidity Management:** Controlled 60-70% humidity to prevent cork drying and label damage"
							},
							{
								id: "4",
								value: "**Vibration-Free Environment:** Specialized flooring and equipment to avoid disturbing wine sediment"
							},
							{
								id: "5",
								value: "**Wine-Specific Security:** Individual wine lockers, restricted access, and wine-knowledgeable staff"
							},
							{
								id: "6",
								value: "**Insurance Options:** Coverage specifically designed for wine collections and their unique valuations"
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Wine Storage CTA",
			noPadding: true,
			modules: [
				{
					type: "inline-cta",
					props: {
						storageType: "Wine",
						subheading: "Make a free reservation in minutes!",
						searchPlaceholder: "Search by city, state, or zip...",
						searchButtonText: "Find Storage",
						image: ctaMan,
						imageAltText: "Find wine storage",
						background: "white"
					}
				}
			]
		},
		{
			title: "Customer Reviews",
			noPadding: true,
			modules: [
				{
					type: "reviews",
					props: reviewsContent
				}
			]
		},
		{
			title: "Wine Storage FAQs",
			noPadding: true,
			modules: [
				{
					type: "faq",
					props: {
						title: "Wine Storage Frequently Asked Questions",
						faqs
					}
				}
			]
		},
		{
			title: "Wine Storage Related Content",
			noPadding: true,
			modules: [
				{
					type: "related-content",
					props: {
						background: "light-blue",
						heading: "Learn More About Wine Storage",
						unitCards: relatedContentCards
					}
				}
			]
		},
		{
			title: "Links to Other PSPs",
			noPadding: true,
			modules: [
				{
					type: "psp-links",
					props: {
						background: "white",
						storageGridContent: pspLinksContent
					}
				}
			]
		},
		{
			title: "Storage Near You",
			noPadding: true,
			modules: [
				{
					type: "nearby-cities",
					props: {
						background: "light-blue",
						storageType: "wine"
					}
				}
			]
		}
	],
	reviews: reviewsContent.testimonials.map((testimonial) => ({
		customerName: testimonial.author,
		reviewerName: testimonial.author,
		stars: "5",
		reviewRating: "5",
		text: testimonial.body,
		reviewText: testimonial.body,
	}))
};

export default PSPWineContent;
